import React, { PropsWithChildren } from "react";

import { captureMessage } from "@sentry/react";
import i18n from "i18next";
import I18nextBrowserLanguageDetector from "i18next-browser-languagedetector";
import { Settings } from "luxon";
import { I18nextProvider, initReactI18next } from "react-i18next";

import en from "@/intl/en/tl.json";
import fr from "@/intl/fr/tl.json";

export const Languages = {
  en: { tl: en },
  fr: { tl: fr },
} satisfies { [key: string]: { tl: typeof fr } };

export type LanguageCode = keyof typeof Languages;

export const languageTranslationMapping = (lang: LanguageCode): string => {
  switch (lang) {
    case "en":
      return "languages.en";
    case "fr":
      return "languages.fr";
  }
};

const SupportedLanguages = Object.keys(Languages);

const i18nInstance = i18n.createInstance();

i18nInstance.on("languageChanged", lng => {
  Settings.defaultLocale = lng;
  document.documentElement.lang = lng;
});

i18nInstance.on("missingKey", (lngs, _, key) => {
  captureMessage(`Missing translation key: ${key} for ${lngs}`, "warning");
});

i18nInstance
  .use(I18nextBrowserLanguageDetector)
  .use(initReactI18next)
  .init({
    resources: Languages,
    debug: false,
    fallbackLng: "fr",
    supportedLngs: SupportedLanguages,
    nonExplicitSupportedLngs: true,
    lowerCaseLng: true,
    ns: "tl",
    defaultNS: "tl",
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
    },
  });

export const I18nWrapper: React.FC<PropsWithChildren> = ({ children }) => {
  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
};
